import { request } from "../../utils/request";


/**
 * 根据任务ID获取说话人列表
 * 接口：GET /admin-api/video/subtitles/speakers
 * @param taskId 任务ID
 * @returns Promise<Speakers.GetSpeakersByTaskResponse>
 */
export const getSpeakersByTaskApi = async (taskId: number): Promise<Speakers.GetSpeakersByTaskResponse> => {
  try {
    // 正在调用 getSpeakersByTaskApi

    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        description: string | null;
        seriesId: number;
        subtitleRespVOS: Array<{
          id: number;
          originText: string;
          score: number;
          originAudio: string;
          originStartTime: string;
          originEndTime: string;
        }> | null;
      }> | null;
      msg: string;
    }>(`/video/subtitles/speakers`, {
      method: 'GET',
      params: { taskId }
    });

    if (response.code !== 0 || !response.data) {
      return {
        success: true,
        message: response.msg || '获取说话人列表成功（无数据）',
        data: []
      };
    }

    // 将新接口返回的数据映射为 Speakers.Speaker[]
    const speakers: Speakers.Speaker[] = response.data.map(item => ({
      id: item.id,
      series_id: item.seriesId,
      task_id: taskId,
      speaker_name: item.speakerName,
      description: item.description || '',
      is_series_speaker: item.isSeriesSpeaker || false,
      subtitles: (item.subtitleRespVOS || []).map(sub => ({
        id: sub.id,
        task_id: taskId,
        origin_text: sub.originText,
        post_text: '',
        post_start_time: sub.originStartTime,
        post_end_time: sub.originEndTime,
        origin_start_time: sub.originStartTime,
        origin_end_time: sub.originEndTime,
        is_episode_voice: false,
        origin_audio: sub.originAudio,
        trans_audio: '',
        subtitle_id: sub.id,
        score: sub.score,
        audio_speed: 1.0,
        volume_gain_db: 1.0
      }))
    }));

    // 返回说话人数据

    return {
      success: true,
      message: '获取说话人列表成功',
      data: speakers
    };
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/video/subtitles/speakers',
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/speakers?taskId=${taskId}`,
      '请求方法': 'GET'
    };

    console.error('=== 获取说话人列表API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);

    throw error;
  }
};

/**
 * 新增说话人（视频编辑页）
 * 接口：POST /admin-api/video/subtitles/add-speaker
 * 请求参数：
 * {
 *   "taskId": 1,
 *   "speakerName": "张三"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 * @param taskId 任务ID
 * @param speakerName 说话人名称
 * @returns Promise<{ code: number; data: boolean; msg: string }>
 */
export const addVideoSpeakerApi = async (
  taskId: number,
  speakerName: string
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/add-speaker`,
      {
        method: "POST",
        body: { taskId, speakerName }
      }
    );
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/subtitles/add-speaker`,
      '任务ID': taskId,
      '说话人名称': speakerName,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/add-speaker`,
      '请求方法': 'POST'
    };
    
    console.error('=== 新增说话人API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 创建说话人（剧集音色库新增角色）
 * 接口：POST /admin-api/video/voice-tone/add-series-speaker
 * @param params 创建说话人参数 { seriesId, speakerName, langCode }
 * @returns Promise<Speakers.CreateSpeakerResponse>
 */
export const createSpeakerApi = async (params: Speakers.CreateSpeakerParams): Promise<Speakers.CreateSpeakerResponse> => {
  try {
    const response = await request<{
      code: number;
      data: {
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        description: string | null;
        seriesId: number;
        subtitleRespVOS: any[] | null;
      };
      msg: string;
    }>(
      `/video/voice-tone/add-series-speaker`,
      {
        method: "POST",
        body: {
          seriesId: params.seriesId,
          speakerName: params.speakerName,
          langCode: params.langCode
        }
      }
    );
    
    // 处理新接口响应格式 { code, data, msg }
    if (response.code === 0 && response.data) {
      // 将新接口返回的数据转换为 Speakers.Speaker 格式
      const speakerData: Speakers.Speaker = {
        id: response.data.id,
        series_id: response.data.seriesId,
        task_id: 0, // 新接口没有 task_id，设为 0
        speaker_name: response.data.speakerName,
        description: response.data.description || '',
        is_series_speaker: response.data.isSeriesSpeaker || true,
        subtitles: response.data.subtitleRespVOS || []
      };
      
      return {
        success: true,
        message: response.msg || '创建说话人成功',
        data: speakerData
      };
    } else {
      throw new Error(response.msg || '创建说话人失败');
    }
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/voice-tone/add-series-speaker`,
      '请求参数': JSON.stringify(params),
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/voice-tone/add-series-speaker`,
      '请求方法': 'POST'
    };
    
    console.error('=== 创建说话人API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 重命名说话人（视频编辑页）
 * 接口：PUT /admin-api/video/subtitles/rename-speaker
 * 请求参数：
 * {
 *   "speakerId": 1,
 *   "newName": "李四"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 * @param speakerId 说话人ID
 * @param newName 新名称
 * @returns Promise<{ code: number; data: boolean; msg: string }>
 */
export const renameVideoSpeakerApi = async (
  speakerId: number,
  newName: string
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/rename-speaker`,
      {
        method: "PUT",
        body: { speakerId, newName }
      }
    );
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/video/subtitles/rename-speaker`,
      '说话人ID': speakerId,
      '新名称': newName,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/rename-speaker`,
      '请求方法': 'PUT'
    };
    
    console.error('=== 重命名说话人API调用错误详情 ===');
    console.table(errorInfo); 
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 更新说话人信息（旧接口，保留用于其他场景）
 * @deprecated 在视频编辑页中请使用 renameVideoSpeakerApi
 * @param speakerId 说话人ID
 * @param params 更新说话人参数
 * @returns Promise<Speakers.UpdateSpeakerResponse>
 */
export const updateSpeakerApi = async (speakerId: number, params: Speakers.UpdateSpeakerParams): Promise<Speakers.UpdateSpeakerResponse> => {
  try {
    const response = await request<any>(
      `/speakers/${speakerId}`,
      {
        method: "PUT",
        body: params
      }
    );
    
    
    // 处理不同的响应格式
    let formattedResponse: Speakers.UpdateSpeakerResponse;
    
    // 情况1: 标准格式 {success: true, data: {...}}
    if (response && typeof response === 'object' && 'success' in response) {
      formattedResponse = response as Speakers.UpdateSpeakerResponse;
    }
    // 情况2: 直接返回说话人对象
    else if (response && typeof response === 'object' && 'id' in response && 'speaker_name' in response) {
      formattedResponse = {
        success: true,
        message: "更新说话人成功",
        data: response as Speakers.Speaker
      };
    }
    // 情况3: 其他格式
    else {
      formattedResponse = {
        success: false,
        message: "响应格式不正确",
        data: {} as Speakers.Speaker
      };
    }
    
    return formattedResponse;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/speakers/${speakerId}`,
      '说话人ID': speakerId,
      '请求参数': JSON.stringify(params),
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/api/speakers/${speakerId}`,
      '请求方法': 'PUT'
    };
    
    console.error('=== 更新说话人API调用错误详情 ===');
    console.table(errorInfo); 
    console.error('完整错误对象:', error);
    
    // 直接抛出错误，不再返回模拟数据
    throw error;
  }
};

/**
 * 获取剧集说话人角色
 * @param seriesId 剧集ID
 * @returns Promise<Speakers.GetSeriesSpeakersResponse | Speakers.Speaker[]>
 *
 * 说明：
 *  当前后端接口 `/speakers/series/{seriesId}` 暂不可用，
 *  按需求“将所有 getSeriesSpeakersApi 调用都注释掉”，
 *  在这里统一屏蔽真实请求并返回空数组，避免到处改调用代码。
 */
export const getSeriesSpeakersApi = async (
  seriesId: number,
  langCode: string
): Promise<Speakers.GetSeriesSpeakersResponse | Speakers.Speaker[]> => {
  try {
    // 确保 seriesId 是数字类型
    const seriesIdNum = typeof seriesId === 'number' ? seriesId : Number(seriesId);
    
    if (isNaN(seriesIdNum)) {
      throw new Error('seriesId 必须是有效的数字');
    }

    // 后端要求必须传 langCode；如果没有则不调用接口
    if (!langCode) {
      return [];
    }

    // 构建请求参数（透传 langCode；即使为空字符串也会携带，便于后端按语种处理）
    const params: any = { seriesId: seriesIdNum, langCode };

    const response = await request<any>(
      `/video/voice-tone/list-by-series`,
      {
        method: "GET",
        params: params
      }
    );

    // 处理响应数据格式转换
    // 新格式：{ code: 0, data: [{ id, speakerName, isSeriesSpeaker, description, seriesId, subtitleRespVOS }] }
    // 旧格式：{ success: true, data: [{ id, speaker_name, is_series_speaker, description, series_id, subtitles }] }
    if (response && response.code === 0 && Array.isArray(response.data)) {
      const speakers: Speakers.Speaker[] = response.data.map((item: any) => ({
        id: item.id,
        series_id: item.seriesId,
        task_id: 0, // 新接口没有 task_id，设为 0
        speaker_name: item.speakerName,
        description: item.description || '',
        is_series_speaker: item.isSeriesSpeaker || false,
        subtitles: item.subtitleRespVOS || [] // 转换字幕数据
      }));

      return {
        success: true,
        message: '获取说话人列表成功',
        data: speakers
      };
    }

    // 如果响应格式不符合预期，返回空数组
    return {
      success: false,
      message: '响应格式不正确',
      data: []
    };
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/video/voice-tone/list-by-series',
      '剧集ID': seriesId,
      '语言代码': langCode || 'N/A',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求方法': 'GET'
    };
    
    console.error('=== 获取剧集说话人角色API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 获取音色库剧集语言列表
 * GET /admin-api/video/voice-tone/query-series-lang-list
 * @param seriesId 剧集ID
 * @returns 剧集语言列表（包含每个语言的说话人数量）
 */
export const getSpeakersSeriesApi = async (seriesId: number): Promise<Speakers.SeriesLangListResponse> => {
  // 确保 seriesId 是数字类型
  const seriesIdNum = typeof seriesId === 'number' ? seriesId : Number(seriesId);
  
  if (isNaN(seriesIdNum)) {
    throw new Error('seriesId 必须是有效的数字');
  }
  
  const response = await request<Speakers.SeriesLangListResponse>(
    `/video/voice-tone/query-series-lang-list`,
    {
      method: "GET",
      params: { seriesId: seriesIdNum }
    }
  );
  return response;
};

/**
 * 查询音色库内层字幕信息
 * GET /video/voice-tone/query-by-role-and-language
 * Query 参数：speakerId, langCode
 * 响应数据：
 * {
 *   code: 0,
 *   data: [
 *     {
 *       id: 0,
 *       originText: "",
 *       score: 0.0,
 *       originAudio: "",
 *       originStartTime: "",
 *       originEndTime: ""
 *     }
 *   ],
 *   msg: ""
 * }
 */
export const queryVoiceToneByRoleAndLanguageApi = async (
  speakerId: number,
  langCode: string
): Promise<{
  code: number;
  data: Array<{
    id: number;
    originText: string;
    score: number;
    originAudio: string;
    originStartTime: string;
    originEndTime: string;
  }>;
  msg: string;
}> => {
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        originText: string;
        score: number;
        originAudio: string;
        originStartTime: string;
        originEndTime: string;
      }>;
      msg: string;
    }>(
      `/video/voice-tone/query-by-role-and-language`,
      {
        method: "GET",
        params: {
          speakerId,
          langCode
        }
      }
    );

    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': '/video/voice-tone/query-by-role-and-language',
      '说话人ID': speakerId,
      '语言代码': langCode,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求方法': 'GET'
    };

    console.error('=== 查询音色库内层字幕信息API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);

    throw error;
  }
};

/**
 * 将字幕添加到剧集音色库
 * 新接口：PUT /video/subtitles/add-to-voice-library
 * 请求参数：
 * {
 *   "subtitleId": 123,
 *   "speakerId": 456
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const promoteSpeakerApi = async (
  speakerId: number,
  subtitleId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/add-to-voice-library`,
      {
        method: "PUT",
        body: {
          subtitleId,
          speakerId
        }
      }
    );

    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * 从音色库中移除字幕（音色库音频）
 * 新接口：PUT /video/voice-tone/remove-by-id
 * 请求参数：
 * {
 *   "id": 123 // 台词id
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const demoteSpeakerSubtitleApi = async (
  subtitleId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/voice-tone/remove-by-id`,
    {
      method: "PUT",
      params: {
        id: subtitleId
      }
    }
  );
  return response;
};

/**
 * 删除说话人
 * DELETE /speakers/{speakerId}
 */
export const deleteSpeakerApi = async (speakerId: number): Promise<{ success?: boolean; message?: string } | void> => {
  const response = await request<{ success?: boolean; message?: string }>(
    `/video/voice-tone/remove-series-speaker`,
    {
      method: "DELETE",
      params: {
        speakerId
      }
    }
  );
  return response;
};



